/*
  MIGA: body scroll lock for the cart drawer.

  Dawn only adds `overflow-hidden` to <body>, which does not stop scrolling
  in iOS WKWebView — the engine behind the in-app browsers in Instagram and
  TikTok, where most of this store's traffic lands. There the page keeps
  scrolling behind the open drawer, and because that scrolling is what makes
  the in-app toolbar slide in and out, it also keeps resizing the viewport
  underneath the drawer.

  The fix is the long-standing position:fixed technique: take the body out of
  flow at a negative offset equal to the current scroll position, so there is
  nothing left to scroll, then put it back and restore the exact offset on
  close. It's deliberately boring — it has worked across every iOS WebView
  version rather than relying on anything recent.

  Idempotent on purpose: renderContents() calls open() again on a drawer
  that's already open, and re-locking then would capture scrollY as 0 (the
  body is already pinned) and scroll the page to the top on close.
*/
const MigaBodyScrollLock = {
  locked: false,
  scrollY: 0,

  lock() {
    if (this.locked) return;
    const body = document.body;
    this.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

    // Desktop only: taking the body out of flow removes the page scrollbar,
    // which would otherwise shift the layout sideways. 0 on touch devices.
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    body.style.position = 'fixed';
    body.style.top = `-${this.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    this.locked = true;
  },

  unlock() {
    if (!this.locked) return;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.paddingRight = '';
    this.locked = false;
    // Only meaningful once the body is back in flow, and explicitly instant:
    // 'auto' beats any inherited scroll-behavior:smooth, which would animate
    // the restore and read as a jump.
    window.scrollTo({ top: this.scrollY, left: 0, behavior: 'auto' });
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    MigaBodyScrollLock.lock();
    // MIGA: lets miga-custom.js react to a genuine drawer open (icon click
    // or real add-to-cart) without re-triggering on quantity changes or the
    // upsell/protection AJAX refreshes, which never call open().
    this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    MigaBodyScrollLock.unlock();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // MIGA: stock Dawn clears "is-empty" off .drawer__inner but not off
    // <cart-drawer> itself, which is what its own
    // `cart-drawer.is-empty .drawer__inner { padding: 0 }` rule keys off.
    // Adding the first item to an empty cart therefore left the class
    // stale and collapsed the drawer's side padding to zero — the product
    // list, progress bar and upsell strip ran edge to edge. Reaching this
    // method always means something was just added, so the cart is
    // non-empty by definition.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
